/**
 * 운영 매뉴얼 챗봇 위젯 (오너/본부 페이지 우하단 고정).
 *
 * 사용법:
 *   <ChatWidget />
 *   - 우하단 떠있는 동그란 버튼 클릭 → 챗 드로어 열림
 *   - 질문 입력 → SSE 스트리밍 응답
 *
 * 필요 환경:
 *   - 백엔드 ANTHROPIC_API_KEY + OPENAI_API_KEY 설정
 *   - chat_documents 테이블에 매뉴얼 임베딩 들어있음
 *   미설정 시 안내 메시지만 표시됨.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChatBubbleLeftEllipsisIcon, XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const getToken = () =>
  localStorage.getItem('hq_access_token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('auth_access_token');

// 운영(PROD)에서는 챗봇 숨김. STG(NTD)/로컬/이루는자리/대구고에서만 노출.
// 운영 도메인 목록에 포함된 hostname이면 위젯 자체를 렌더하지 않음.
const PROD_HOSTNAMES = [
  'app.newattend.com',
  'hq.newattend.com',
  'studyroom.newattend.com',
  'newattend.com',
  'www.newattend.com',
];

const isChatbotHidden = (() => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return PROD_HOSTNAMES.includes(host);
})();

export const ChatWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [healthMsg, setHealthMsg] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // 자동 스크롤
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 처음 열 때 health 체크
  useEffect(() => {
    if (!open || healthMsg !== null) return;
    (async () => {
      try {
        const token = getToken();
        const r = await fetch(`${API_BASE_URL}/api/v1/chatbot/health`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await r.json();
        if (!data.ready) {
          setHealthMsg(`⚠️ ${data.message}`);
        } else {
          setHealthMsg('');
        }
      } catch {
        setHealthMsg('⚠️ 챗봇 상태 확인 실패');
      }
    })();
  }, [open, healthMsg]);

  const send = async () => {
    const q = input.trim();
    if (!q || streaming) return;
    setInput('');
    setStreaming(true);

    const newMessages = [...messages, { role: 'user' as const, content: q }];
    setMessages([...newMessages, { role: 'assistant', content: '' }]);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE_URL}/api/v1/chatbot/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question: q,
          history: messages.slice(-6),
        }),
        signal: ac.signal,
      });

      if (!resp.ok || !resp.body) {
        let errMsg = `❌ 오류 (${resp.status})`;
        if (resp.status === 401) {
          errMsg = '⚠️ 로그인이 만료되었습니다. 페이지를 새로고침(F5)하거나 다시 로그인해주세요.';
        } else if (resp.status === 429) {
          errMsg = '⏳ 너무 많은 요청. 잠시 후 다시 시도해주세요.';
        }
        setMessages([...newMessages, { role: 'assistant', content: errMsg }]);
        setStreaming(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE: 이벤트 단위는 \n\n 으로 구분됨
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';
        for (const ev of events) {
          // 한 이벤트는 여러 'data: ...' 줄로 구성될 수 있음 (현재는 한 줄)
          const dataLines = ev
            .split('\n')
            .filter((l) => l.startsWith('data: '))
            .map((l) => l.slice(6));
          if (dataLines.length === 0) continue;
          const raw = dataLines.join('\n');
          if (raw === '[DONE]') continue;
          // JSON {"text": "..."} 으로 인코딩되어 있음
          try {
            const parsed = JSON.parse(raw);
            if (typeof parsed.text === 'string') {
              assistantText += parsed.text;
              setMessages([...newMessages, { role: 'assistant', content: assistantText }]);
            }
          } catch {
            // 레거시: 그냥 문자열인 경우
            assistantText += raw;
            setMessages([...newMessages, { role: 'assistant', content: assistantText }]);
          }
        }
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        setMessages([...newMessages, { role: 'assistant', content: `❌ ${e?.message || '오류'}` }]);
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const clear = () => {
    setMessages([]);
  };

  // 운영 환경에서는 위젯 숨김 (모든 hooks 호출 후 안전하게 early return)
  if (isChatbotHidden) {
    return null;
  }

  return (
    <>
      {/* 플로팅 버튼 */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg flex items-center justify-center transition-all hover:scale-110"
          aria-label="운영 도우미 챗봇 열기"
        >
          <ChatBubbleLeftEllipsisIcon className="w-7 h-7" />
        </button>
      )}

      {/* 챗 드로어 */}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col border border-slate-200">
          {/* 헤더 */}
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-green-600 text-white rounded-t-2xl">
            <div>
              <div className="font-bold text-sm">💡 운영 도우미</div>
              <div className="text-xs text-green-100">매뉴얼 기반 AI 챗봇</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={clear}
                className="px-2 py-1 text-xs rounded hover:bg-white/20"
                title="대화 초기화"
              >
                초기화
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-white/20"
                aria-label="닫기"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* 헬스 안내 */}
          {healthMsg && (
            <div className="px-4 py-2 bg-yellow-50 text-yellow-800 text-xs border-b border-yellow-200">
              {healthMsg}
            </div>
          )}

          {/* 메시지 영역 */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center text-sm text-slate-500 mt-8">
                <div className="text-4xl mb-3">👋</div>
                <div className="mb-2">안녕하세요!</div>
                <div className="text-xs">
                  운영 중 궁금한 것을 물어보세요.
                  <br />
                  매뉴얼 기반으로 답변드립니다.
                </div>
                <div className="mt-6 text-xs text-slate-400">
                  예시:
                  <br />
                  • "신규 학생 등록 어떻게 해?"
                  <br />
                  • "벌점 차감 절차"
                  <br />
                  • "사유서 승인 방법"
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap break-words ${
                    m.role === 'user'
                      ? 'bg-green-600 text-white rounded-br-sm'
                      : 'bg-white text-slate-800 rounded-bl-sm border border-slate-200'
                  }`}
                >
                  {m.content || (m.role === 'assistant' && streaming ? '...' : '')}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* 입력창 */}
          <div className="p-3 border-t border-slate-200">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={2}
                placeholder="질문을 입력하세요... (Enter 전송, Shift+Enter 줄바꿈)"
                className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg resize-none focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                disabled={streaming}
              />
              <button
                onClick={send}
                disabled={!input.trim() || streaming}
                className="w-10 h-10 rounded-lg bg-green-600 hover:bg-green-700 text-white flex items-center justify-center disabled:opacity-40 transition-colors"
                aria-label="전송"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
